import { useState, type CSSProperties } from 'react';
import TablePagination from '@mui/material/TablePagination';

import { COLORS } from '../../IndividualPlan/tokens';
import { TableWrapper } from './SchoolComparisonTable.styled';

const DEFAULT_ROWS_PER_PAGE = 10;
const ROWS_PER_PAGE_OPTIONS = [10, 25, 50];

export type ScoreUnit = 'proficiency_level' | 'scale_score';

export interface School {
  school_name: string;
  el_count: number;
  total_enrollment?: number | null;
  el_share_of_enrollment?: number | null;
  newcomer_count: number;
  newcomer_share_of_school_els: number;
  composite_growth_scale_score_delta?: number | null;
  composite_growth_proficiency_level_delta?: number | null;
  composite_growth_last_year?: number | null;
  reclassification_rate: number | null;
  ltel_count: number;
  ltel_share_of_school_els: number;
  current_member_count?: number | null;
}

export interface SchoolComparisonTableProps {
  schools: School[];
  districtReclassificationRate: number | null;
  districtCompositeGrowthScaleScoreDelta: number | null;
  districtCompositeGrowthProficiencyLevelDelta: number | null;
  unit: ScoreUnit;
}

const EM_DASH = '—';

const BORDER = `1px solid ${COLORS.divider}`;
const DISTRICT_ROW_BG = '#CCE8FA';

const FOOTER_SX = {
  borderTop: BORDER,
  '& .MuiTablePagination-toolbar': { minHeight: 52, paddingRight: '16px' },
  '& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows': {
    fontSize: 12,
    color: COLORS.muted,
  },
} as const;

const HEADER_CELL_STYLE: CSSProperties = {
  backgroundColor: '#F6F6F8',
  padding: '16px 16px 8px',
  textAlign: 'left',
  fontSize: 16,
  fontWeight: 700,
  color: COLORS.ink,
  borderBottom: BORDER,
  verticalAlign: 'bottom',
};
const HEADER_CELL_FIRST_STYLE: CSSProperties = { ...HEADER_CELL_STYLE, paddingLeft: 24 };

const BODY_CELL_STYLE: CSSProperties = {
  padding: '8px 16px',
  fontSize: 16,
  fontWeight: 500,
  color: COLORS.ink,
  borderBottom: BORDER,
};
const BODY_CELL_FIRST_STYLE: CSSProperties = { ...BODY_CELL_STYLE, paddingLeft: 24 };

const DISTRICT_CELL_STYLE: CSSProperties = { ...BODY_CELL_STYLE, backgroundColor: DISTRICT_ROW_BG };
const DISTRICT_CELL_FIRST_STYLE: CSSProperties = { ...DISTRICT_CELL_STYLE, paddingLeft: 24 };

const TABLE_STYLE: CSSProperties = { width: '100%', borderCollapse: 'collapse' };

const formatPercent = (fraction?: number | null): string =>
  fraction === null || fraction === undefined ? EM_DASH : `${Math.round(fraction * 100)}%`;

const formatScaleScoreGrowth = (delta?: number | null): string =>
  delta === null || delta === undefined ? EM_DASH : `${delta >= 0 ? '+' : ''}${delta} pts`;

const formatProficiencyGrowth = (delta?: number | null): string =>
  delta === null || delta === undefined
    ? EM_DASH
    : `${delta >= 0 ? '+' : ''}${delta.toFixed(1)} levels`;

const formatGrowthByUnit = (
  unit: ScoreUnit,
  scaleScoreDelta?: number | null,
  proficiencyLevelDelta?: number | null,
): string =>
  unit === 'scale_score'
    ? formatScaleScoreGrowth(scaleScoreDelta)
    : formatProficiencyGrowth(proficiencyLevelDelta);

const formatCompositeGrowth = (school: School, unit: ScoreUnit): string =>
  formatGrowthByUnit(
    unit,
    school.composite_growth_scale_score_delta,
    school.composite_growth_proficiency_level_delta,
  );

const formatCountWithShare = (count: number, share?: number | null): string =>
  `${count} (${formatPercent(share)})`;

export const SchoolComparisonTable = ({
  schools,
  districtReclassificationRate,
  districtCompositeGrowthScaleScoreDelta,
  districtCompositeGrowthProficiencyLevelDelta,
  unit,
}: SchoolComparisonTableProps) => {
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(DEFAULT_ROWS_PER_PAGE);

  const memberDenominated = schools.some((school) => school.current_member_count != null);
  const shareLabel = memberDenominated ? '% of current members' : '% of ELs';
  const newcomerShareHeader = `Newcomers (${shareLabel})`;
  const ltelShareHeader = `LTEL count (${shareLabel})`;

  const rows = schools
    .filter((school) => school.el_count > 0)
    .sort((a, b) => a.school_name.localeCompare(b.school_name));

  const pageStart = page * rowsPerPage;
  const pagedRows = rows.slice(pageStart, pageStart + rowsPerPage);

  return (
    <TableWrapper>
      <table data-testid="school-comparison-table" style={TABLE_STYLE}>
        <thead>
          <tr>
            <th scope="col" style={HEADER_CELL_FIRST_STYLE}>School</th>
            <th scope="col" style={HEADER_CELL_STYLE}>EL count (% of enrollment)</th>
            <th scope="col" style={HEADER_CELL_STYLE}>{newcomerShareHeader}</th>
            <th scope="col" style={HEADER_CELL_STYLE}>Composite growth</th>
            <th scope="col" style={HEADER_CELL_STYLE}>Exit rate</th>
            <th scope="col" style={HEADER_CELL_STYLE}>{ltelShareHeader}</th>
          </tr>
        </thead>
        <tbody>
          <tr data-testid="district-average-row">
            <td style={DISTRICT_CELL_FIRST_STYLE}>District average</td>
            <td style={DISTRICT_CELL_STYLE}>{EM_DASH}</td>
            <td style={DISTRICT_CELL_STYLE}>{EM_DASH}</td>
            <td data-testid="district-growth-cell" style={DISTRICT_CELL_STYLE}>
              {formatGrowthByUnit(
                unit,
                districtCompositeGrowthScaleScoreDelta,
                districtCompositeGrowthProficiencyLevelDelta,
              )}
            </td>
            <td data-testid="district-reclassification-cell" style={DISTRICT_CELL_STYLE}>
              {districtReclassificationRate === null
                ? EM_DASH
                : formatPercent(districtReclassificationRate)}
            </td>
            <td style={DISTRICT_CELL_STYLE}>{EM_DASH}</td>
          </tr>
          {rows.length === 0 ? (
            <tr>
              <td data-testid="empty-state" colSpan={6} style={BODY_CELL_FIRST_STYLE}>
                No schools with EL students this year.
              </td>
            </tr>
          ) : (
            pagedRows.map((school) => (
              <tr key={school.school_name} data-testid="school-row">
                <td data-testid="school-name-cell" style={BODY_CELL_FIRST_STYLE}>
                  {school.school_name}
                </td>
                <td data-testid="el-cell" style={BODY_CELL_STYLE}>
                  {formatCountWithShare(school.el_count, school.el_share_of_enrollment)}
                </td>
                <td data-testid="newcomer-cell" style={BODY_CELL_STYLE}>
                  {formatCountWithShare(school.newcomer_count, school.newcomer_share_of_school_els)}
                </td>
                <td data-testid="composite-growth-cell" style={BODY_CELL_STYLE}>
                  {formatCompositeGrowth(school, unit)}
                </td>
                <td data-testid="reclassification-cell" style={BODY_CELL_STYLE}>
                  {school.reclassification_rate === null
                    ? EM_DASH
                    : formatPercent(school.reclassification_rate)}
                </td>
                <td data-testid="ltel-cell" style={BODY_CELL_STYLE}>
                  {formatCountWithShare(school.ltel_count, school.ltel_share_of_school_els)}
                </td>
              </tr>
            ))
          )}
        </tbody>
      </table>
      <TablePagination
        component="div"
        data-testid="school-comparison-pagination"
        count={rows.length}
        page={page}
        onPageChange={(_, nextPage) => setPage(nextPage)}
        rowsPerPage={rowsPerPage}
        rowsPerPageOptions={ROWS_PER_PAGE_OPTIONS}
        onRowsPerPageChange={(event) => {
          setRowsPerPage(parseInt(event.target.value, 10));
          setPage(0);
        }}
        sx={FOOTER_SX}
      />
    </TableWrapper>
  );
};
